import React, { createContext, useContext, useMemo, useState } from "react";

export type StickyContextType = {
  id: symbol;
  topItems: StickyContextItem[];
  bottomItems: StickyContextItem[];
  updateItem: (element: HTMLElement, item: StickyContextItem | null) => void;
};

export type StickyContextItem = {
  element: HTMLElement;
  height: number;
  side: "top" | "bottom";
};

export const StickyContext = createContext<StickyContextType | null>(null);

export function StickyProvider({ children }: { children: React.ReactNode }) {
  const id = useMemo(() => Symbol("StickyProvider"), []);

  const [topItemsMap, setTopItemsMap] = useState<
    ReadonlyMap<HTMLElement, StickyContextItem>
  >(new Map());

  const [bottomItemsMap, setBottomItemsMap] = useState<
    ReadonlyMap<HTMLElement, StickyContextItem>
  >(new Map());

  const topItems = useMemo(() => [...topItemsMap.values()], [topItemsMap]);
  const bottomItems = useMemo(
    () => [...bottomItemsMap.values()],
    [bottomItemsMap],
  );

  const updateItem = useMemo(() => {
    const updateItem = (
      element: HTMLElement,
      item: StickyContextItem | null,
    ) => {
      for (const side of ["top", "bottom"] as const) {
        const setItemsMap = side === "top" ? setTopItemsMap : setBottomItemsMap;

        setItemsMap((itemsMap) => {
          let newItemsMap: Map<HTMLElement, StickyContextItem>;

          if (item?.side === side) {
            const existingItem = itemsMap.get(element);
            if (existingItem === item) return itemsMap;
            newItemsMap = new Map(itemsMap);
            newItemsMap.set(element, item);
          } else {
            if (!itemsMap.has(element)) return itemsMap;
            newItemsMap = new Map(itemsMap);
            newItemsMap.delete(element);
          }

          const sortedItems = [...newItemsMap].sort(
            ([aEl], [bEl]) =>
              aEl.getBoundingClientRect()[side] -
              bEl.getBoundingClientRect()[side],
          );

          if (side === "bottom") {
            sortedItems.reverse();
          }

          newItemsMap = new Map(sortedItems);

          return newItemsMap;
        });
      }
    };

    return updateItem;
  }, []);

  const context = useMemo<StickyContextType>(
    () => ({
      id,
      topItems,
      bottomItems,
      updateItem,
    }),
    [id, topItems, bottomItems, updateItem],
  );

  return (
    <StickyContext.Provider value={context}>{children}</StickyContext.Provider>
  );
}

export function useStickyContext() {
  const context = useContext(StickyContext);
  if (!context) {
    throw new Error("useStickyContext must be used within a StickyProvider");
  }
  return context;
}
